// Final AffiliateList.jsx - Safe, Bulletproof Version

import React, { useEffect, useState } from "react";
import AuthService from "../../../api/services/AuthService";
import { alertErrorMessage, alertSuccessMessage } from "../../../customComponent/CustomAlertMessage";
import LoaderHelper from "../../../customComponent/Loading/LoaderHelper";
import DataTableBase from "../../../customComponent/DataTable";
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import ReferralTree from './common';

const PayoutList = () => {
  const [affiliates, setAffiliates] = useState([]);
  const [allData, setAllData] = useState([]);
  const [currency, setCurrency] = useState("INR");
  const [walletBalance, setWalletBalance] = useState(0);
  const [planId, setPlanId] = useState("");
  const [packageList, setPackageList] = useState([]);
  const [viewModalData, setViewModalData] = useState(null);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editInvestmentData, setEditInvestmentData] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [detailsModalData, setDetailsModalData] = useState(null);
  const [showPayoutModal, setShowPayoutModal] = useState(false);
  const [payoutModalUser, setPayoutModalUser] = useState(null);
  const [selectedInvestmentForEdit, setSelectedInvestmentForEdit] = useState({
    self_roi_percent: '',
    currency: '',
    plan_id: '',
    amount: '',
    investmentDate: '',
    uplines: [],
    remarks: '',
    investment_id: '',
    investor: ''
  });
  const [showFundModal, setShowFundModal] = useState(false);
  const [fundAmount, setFundAmount] = useState('');
  const [removeAmount, setRemoveAmount] = useState('');
  const [fundWalletBalance, setFundWalletBalance] = useState(0);
  const [updateLockedBalance, setUpdateLockedBalance] = useState(true);
  const [fundSource, setFundSource] = useState('wallet');
  const [operationType, setOperationType] = useState('add');
  const [showStatusConfirmModal, setShowStatusConfirmModal] = useState(false);
  const [pendingStatusChange, setPendingStatusChange] = useState(null);
  const [releaseToWallet, setReleaseToWallet] = useState(true);
  const [deductFromWallet, setDeductFromWallet] = useState(true);

  // Custom scrollbar styles
  const customScrollbarStyles = `
    .custom-scrollbar::-webkit-scrollbar {
      width: 8px;
    }
    
    .custom-scrollbar::-webkit-scrollbar-track {
      background: #f1f1f1;
      border-radius: 10px;
    }
    
    .custom-scrollbar::-webkit-scrollbar-thumb {
      background: #888;
      border-radius: 10px;
    }
    
    .custom-scrollbar::-webkit-scrollbar-thumb:hover {
      background: #555;
    }
    
    /* Firefox */
    .custom-scrollbar {
      scrollbar-width: thin;
      scrollbar-color: #888 #f1f1f1;
    }
  `;

  useEffect(() => {
    fetchAffiliates();
    fetchAllPackageList();
  }, []);

  const fetchAllPackageList = async () => {
    try {
      const result = await AuthService.allPackageList();
      if (result.success) {
        setPackageList(result?.data[0] || []);
      }
    } catch {
      alertErrorMessage("Failed to fetch packages");
    }
  };

  const fetchAffiliates = async () => {
    LoaderHelper.loaderStatus(true);
    try {
      const result = await AuthService.getaffiliateList();
      if (result.success) {
        setAffiliates(result.data || []);
        setAllData(result.data || []);
      } else {
        alertErrorMessage(result.message);
      }
    } catch {
      alertErrorMessage("Failed to fetch affiliates");
    } finally {
      LoaderHelper.loaderStatus(false);
    }
  };



  const openEditModal = (investment) => {
    if (!investment?._id) return;
    const roiDistribution = (investment.roi_distribution || []).map(roi => ({
      ...roi,
      roi_percent: parseFloat(roi.roi_percent || 0),
    }));

    setEditInvestmentData({
      investmentId: investment._id,
      amount: parseFloat(typeof investment.amount === 'object' ? investment.amount?.$numberDecimal : investment.amount || 0),
      self_roi_percent: parseFloat(investment.self_roi_percent || 0),
      roi_distribution: roiDistribution
    });

    setEditModalVisible(true);
  };

  const handleEditInvestmentSubmit = async () => {
    try {
      LoaderHelper.loaderStatus(true);
      const payload = {
        investmentId: editInvestmentData.investmentId,
        amount: parseFloat(editInvestmentData.amount),
        self_roi_percent: parseFloat(editInvestmentData.self_roi_percent),
        roi_distribution: editInvestmentData.roi_distribution.map(up => ({
          level: up.level,
          userId: up.user._id,
          percent: parseFloat(up.roi_percent) || 0
        })),
        remarks: editInvestmentData.remarks || '',
        investmentDate: editInvestmentData.investmentDate || new Date().toISOString().split('T')[0]
      };
      const response = await AuthService.editAffiliateInvestment(payload);
      if (response.success) {
        alertSuccessMessage("Investment updated successfully");
        fetchAffiliates();
        setEditModalVisible(false);
        setViewModalData(null)
      } else {
        alertErrorMessage(response.message);
      }
    } catch {
      alertErrorMessage("Failed to update investment");
    } finally {
      LoaderHelper.loaderStatus(false);
    }
  };

  const loadInvestments = async (userId) => {
    LoaderHelper.loaderStatus(true);
    try {
      const result = await AuthService.loadInvestments(userId);
      if (result.success) {
        setDetailsModalData(prev => ({ ...prev, investments: result?.affiliateInvestments || [] }));
      }
    } catch {
      alertErrorMessage("Failed to fetch packages");
    } finally {
      LoaderHelper.loaderStatus(false);
    }
  };

  const handleFundUpdate = async () => {
    try {
      const fundAmountValue = parseFloat(fundAmount || 0);
      const removeAmountValue = parseFloat(removeAmount || 0);
      const walletBalanceValue = parseFloat(fundWalletBalance || walletBalance);
      const currentAmount = parseFloat(
        typeof selectedInvestmentForEdit.amount === 'object' 
          ? selectedInvestmentForEdit.amount?.$numberDecimal || 0
          : selectedInvestmentForEdit.amount || 0
      );

      // Validation based on operation type
      if (operationType === 'add') {
        if (!fundAmount || fundAmountValue <= 0) {
          alertErrorMessage('Please enter an amount greater than 0');
          return;
        }
        // Validation for wallet balance source: fundAmount must not exceed wallet balance
        if (fundSource === 'wallet' && fundAmountValue > walletBalanceValue) {
          alertErrorMessage(`Amount cannot exceed wallet balance of ${walletBalanceValue} ${selectedInvestmentForEdit.currency}`);
          return;
        }
      } else if (operationType === 'remove') {
        if (!removeAmount || removeAmountValue <= 0) {
          alertErrorMessage('Please enter an amount greater than 0 to remove');
          return;
        }
        // Validation: Remove amount cannot exceed current investment amount
        if (removeAmountValue > currentAmount) {
          alertErrorMessage(`Amount to remove cannot exceed current investment amount of ${currentAmount.toFixed(2)} ${selectedInvestmentForEdit.currency}`);
          return;
        }
      }

      LoaderHelper.loaderStatus(true);

      const newTotalAmount = operationType === 'add' 
        ? currentAmount + fundAmountValue 
        : currentAmount - removeAmountValue;

      // Validation: New total amount cannot be negative
      if (newTotalAmount < 0) {
        alertErrorMessage('Total investment amount cannot be negative');
        LoaderHelper.loaderStatus(false);
        return;
      }

      const payload = {
        investmentId: selectedInvestmentForEdit.id,
        amount: newTotalAmount,
        addedAmount: operationType === 'add' ? fundAmountValue : 0,
        removedAmount: operationType === 'remove' ? removeAmountValue : 0,
        updateLockedBalance: updateLockedBalance,
        fundSource: fundSource,
        operationType: operationType
      };

      const response = await AuthService.updateAffiliateInvestmentAmount(payload);
      
      if (response.success) {
        setSelectedInvestmentForEdit({
          ...selectedInvestmentForEdit,
          amount: newTotalAmount.toString()
        });
        setShowFundModal(false);
        setFundAmount('');
        setRemoveAmount('');
        setUpdateLockedBalance(true);
        setFundSource('wallet');
        setOperationType('add');
        const message = operationType === 'add'
          ? `Successfully added ${fundAmountValue} ${selectedInvestmentForEdit.currency} to investment`
          : `Successfully removed ${removeAmountValue} ${selectedInvestmentForEdit.currency} from investment`;
        alertSuccessMessage(message);
        loadInvestments(detailsModalData.user._id);
      } else {
        alertErrorMessage(response.message || 'Failed to update investment amount');
      }
    } catch (error) {
      alertErrorMessage('Failed to update investment amount');
    } finally {
      LoaderHelper.loaderStatus(false);
    }
  };

  const handleStatusToggle = async () => {
    try {
      const newStatus = pendingStatusChange.newStatus;
      const investmentAmount = parseFloat(
        typeof selectedInvestmentForEdit.amount === 'object' 
          ? selectedInvestmentForEdit.amount?.$numberDecimal || 0
          : selectedInvestmentForEdit.amount || 0
      );
      const walletBalanceValue = parseFloat(fundWalletBalance || walletBalance);

      // If activating and deducting from wallet, check wallet balance
      if (newStatus === 'ACTIVE' && deductFromWallet) {
        if (investmentAmount > walletBalanceValue) {
          alertErrorMessage(`Insufficient wallet balance. Required: ${investmentAmount} ${selectedInvestmentForEdit.currency}, Available: ${walletBalanceValue} ${selectedInvestmentForEdit.currency}`);
          setShowStatusConfirmModal(false);
          return;
        }
      }

      LoaderHelper.loaderStatus(true);

      const payload = {
        investmentId: selectedInvestmentForEdit.id,
        status: newStatus,
        releaseToWallet: newStatus === 'INACTIVE' ? releaseToWallet : false,
        deductFromWallet: newStatus === 'ACTIVE' ? deductFromWallet : false
      };

      const response = await AuthService.updateStatusAffiliateRelease(payload);
      
      if (response.success) {
        setSelectedInvestmentForEdit({
          ...selectedInvestmentForEdit,
          status: newStatus
        });
        setShowStatusConfirmModal(false);
        setPendingStatusChange(null);
        setReleaseToWallet(true);
        setDeductFromWallet(true);
        alertSuccessMessage(`Investment ${newStatus === 'ACTIVE' ? 'activated' : 'deactivated'} successfully`);
        loadInvestments(detailsModalData.user._id);
      } else {
        alertErrorMessage(response.message || 'Failed to update status');
      }
    } catch (error) {
      alertErrorMessage('Failed to update investment status');
    } finally {
      LoaderHelper.loaderStatus(false);
    }
  };

  const openViewModal = (user) => {
    setViewModalData({ user, investments: user.investments || [] });
  };

  const openDetailsModal = (user) => {
    loadInvestments(user._id);
    setDetailsModalData({ user, investments: user.investments || [] });
    setSelectedInvestmentForEdit(null);
    setShowDetailsModal(true);

  };

  const openPayoutModal = (user) => {
    setPayoutModalUser(user);
    setShowPayoutModal(true);
  };

  const columns = [
    { name: 'Sr No.', selector: (row, idx) => idx + 1, width: '80px' },
    { name: 'Name', selector: row => `${row.firstName} ${row.lastName}` },
    { name: 'Email', selector: row => row.emailId, width: '200px' },
    { name: 'Total Investment', selector: row => parseFloat(row.total_investment || 0).toFixed(2), width: '140px' },
    { name: 'Self ROI Returned', selector: row => parseFloat(row.total_self_roi_returned || 0).toFixed(2), width: '160px' },
    { name: 'Total ROI to Uplines', selector: row => parseFloat(row.total_upline_roi_returned || 0).toFixed(2), width: '180px' },
    // { name: 'Status', selector: row => (row.status), width: '180px' },
    {
      name: 'Actions',
      cell: row => (
        <>
          {/* <button className="btn btn-sm btn-info me-2" onClick={() => openViewModal(row)}>View Details</button> */}
          <button className="btn btn-sm btn-info" onClick={() => openPayoutModal(row)}>Payout Details</button>
        </>
      )
    }
  ];
  const handleStatus = async (_id, status) => {

    await AuthService.updateStatusAffiliate(_id, status).then(
      async (result) => {
        if (result.success) {
          try {
            fetchAffiliates();
            setViewModalData(null)
            alertSuccessMessage(result.message);
          } catch (error) {
            alertErrorMessage(error);
          }

        } else {
          alertErrorMessage(result.message);
        }
      }
    );
  };



  const downloadAffiliateList = (affiliateList, format = 'excel') => {
    if (!affiliateList?.length) {
      alert("No data available to download.");
      return;
    }

    const finalRows = [];

    affiliateList.forEach(user => {
      const userName = `${user.firstName || ''} ${user.lastName || ''}`.trim();
      const email = user.emailId || '';

      user.investments?.forEach((inv) => {
        const amount = typeof inv.amount === 'object' ? inv.amount?.$numberDecimal : inv.amount || 0;
        const selfROI = inv.self_roi_percent || 0;
        const roiDist = inv.roi_distribution || [];

        if (roiDist.length === 0) {
          finalRows.push({
            Name: userName,
            Email: email,
            Investment: amount,
            SelfROI: selfROI,
            Level: '',
            ROIPercent: '',
            UplineName: '',
            UplineEmail: ''
          });
        } else {
          roiDist.forEach((roi, index) => {
            const upline = roi.user || {};
            finalRows.push({
              Name: index === 0 ? userName : '',
              Email: index === 0 ? email : '',
              Investment: index === 0 ? amount : '',
              SelfROI: index === 0 ? selfROI : '',
              Level: roi.level,
              ROIPercent: roi.roi_percent,
              UplineName: `${upline.firstName || ''} ${upline.lastName || ''}`.trim(),
              UplineEmail: upline.emailId || ''
            });
          });
        }
      });
    });

    if (format === 'excel') {
      const worksheet = XLSX.utils.json_to_sheet(finalRows);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Affiliate ROI');
      const wbout = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
      saveAs(new Blob([wbout], { type: "application/octet-stream" }), 'Affiliate_Investments_ROI.xlsx');
    }

    if (format === 'pdf') {
      const doc = new jsPDF();
      const tableData = finalRows.map(row => [
        row.Name,
        row.Email,
        row.Investment,
        row.SelfROI,
        row.Level,
        row.ROIPercent,
        row.UplineName,
        row.UplineEmail
      ]);

      doc.text('Affiliate Investment ROI Details', 14, 15);
      doc.autoTable({
        startY: 20,
        head: [['User Name', 'Email', 'Investment Amount', 'Self ROI %', 'Level', 'ROI %', 'Upline Name', 'Upline Email']],
        body: tableData,
        styles: { fontSize: 9 },
        headStyles: { fillColor: [0, 102, 204] }
      });

      doc.save('Affiliate_Investments_ROI.pdf');
    }
  };



  const searchPackages = (e) => {
    const term = e.target.value.toLowerCase();
    const keysToSearch = ["emailId", "firstName", "lastName",];
    const filtered = allData.filter(obj =>
      keysToSearch.some(key =>
        obj[key]?.toString().toLowerCase().includes(term)
      )
    );
    setAffiliates(filtered);
  };

  const getWalletBalance = async (userId) => {
    try {
      LoaderHelper.loaderStatus(true);
      const result = await AuthService.getWalletBalance(currency, userId);
      if (result?.success) {
        const balance = result?.data?.balance || 0;
        setWalletBalance(balance);
        return balance;
      }
      return 0;
    } catch (error) {
      return 0;
    }
    finally { LoaderHelper.loaderStatus(false); }
  };

  return (
    <div id="layoutSidenav_content">
      <style>{customScrollbarStyles}</style>
      <div className="container-xl px-4 mt-4">
        <div className="card">
          <div className="card-header d-flex justify-content-between align-items-center">
            <h5>Affiliate List</h5>
            <div className="col-5">
              <input style={{ border: "1px solid #ccc" }}
                className="form-control form-control-solid"
                type="text"
                placeholder="Search here..."
                onChange={searchPackages}
              />
            </div>
            <div>
              <button className="btn btn-success btn-sm mx-2" onClick={() => downloadAffiliateList(affiliates, 'excel')}>Export Excel</button>
              <button className="btn btn-danger btn-sm" onClick={() => downloadAffiliateList(affiliates, 'pdf')}>Export PDF</button>
            </div>

          </div>


          <div className="card-body">
            <DataTableBase columns={columns} data={affiliates} pagination={true} />
          </div>
        </div>

        {viewModalData && (
          <div className="modal fade show d-block" style={{ background: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog modal-lg">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">
                    Investment Details for {viewModalData.user.firstName} {viewModalData.user.lastName}
                  </h5>
                  <button className="btn-close" onClick={() => setViewModalData(null)}></button>
                </div>
                <div className="modal-body">
                  {viewModalData && (
                    <div className="modal fade show d-block" style={{ background: 'rgba(0,0,0,0.5)' }}>
                      <div className="modal-dialog modal-lg">
                        <div className="modal-content">
                          <div className="modal-header">
                            <h5 className="modal-title">
                              Investment Details for {viewModalData.user.firstName} {viewModalData.user.lastName}
                            </h5>
                            <button className="btn-close" onClick={() => setViewModalData(null)}></button>
                          </div>
                          <div className="modal-body">
                            {(Array.isArray(viewModalData.investments) && viewModalData.investments.length > 0) ? (
                              viewModalData.investments.map((inv, idx) => {
                                const amount = typeof inv.amount === 'object'
                                  ? parseFloat(inv.amount?.$numberDecimal || 0)
                                  : parseFloat(inv.amount || 0);

                                return (
                                  <div key={idx} className="border p-3 mb-4 rounded">

                                    <div className="my-2 d-flex justify-content-end">
                                      <h6 className="mx-4">  Update status :</h6>

                                      <button className="btn btn-success btn-sm  me-2" onClick={() => { handleStatus(inv?._id, 'ACTIVE') }} >Active</button>
                                      <button className="btn btn-warning btn-sm  me-2" onClick={() => { handleStatus(inv?._id, 'COMPLETED') }} >Complete</button>
                                      <button className="btn btn-danger btn-sm  me-2" onClick={() => { handleStatus(inv?._id, 'CANCELLED') }} >Cancel</button>
                                    </div>
                                    <h6 className="mb-3">Investment #{idx + 1}</h6>
                                    <p className={`${inv?.status === "ACTIVE" ? "text-success" : inv?.status === "COMPLETED" ? "text-warning" : "text-danger"}`}><strong>Status:</strong> {inv?.status}</p>
                                    <p><strong>Currency:</strong> {inv.currency}</p>
                                    <p><strong>Amount:</strong> {amount.toFixed(2)}</p>
                                    <p><strong>Self ROI :</strong> {inv.self_roi_percent}%</p>
                                    <p><strong>Self ROI Returned:</strong> {parseFloat(inv.total_self_roi_returned || 0).toFixed(2)}</p>
                                    <p><strong>Total ROI to Uplines:</strong> {parseFloat(
                                      (inv.roi_distribution || []).reduce((sum, r) => sum + parseFloat(r.total_returned?.$numberDecimal || r.total_returned || 0), 0)
                                    ).toFixed(2)}</p>

                                    <h6 className="mt-3">Upline ROI Distribution:</h6>
                                    <ul className="mb-3">
                                      {(inv.roi_distribution || []).map((roi, i) => (
                                        <li key={i}>
                                          <strong>Level {roi.level}</strong> — {roi.user?.firstName} {roi.user?.lastName} ({roi.user?.emailId})<br />
                                          ROI : {roi.roi_percent}%
                                          {/* <br /> Returned: {parseFloat(roi.total_returned?.$numberDecimal || 0).toFixed(2)} */}
                                        </li>
                                      ))}
                                    </ul>

                                    <div className="text-end">
                                      <button className="btn btn-sm btn-warning" onClick={() => openEditModal(inv)}>Edit</button>
                                    </div>
                                  </div>
                                );
                              })
                            ) : (
                              <p>No investment records found.</p>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                </div>
              </div>
            </div>
          </div>
        )}


        {editModalVisible && editInvestmentData && (
          <div className="modal fade show d-block" style={{ background: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog modal-lg">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Edit Investment</h5>
                  <button className="btn-close" onClick={() => setEditModalVisible(false)}></button>
                </div>
                <div className="modal-body">
                  <div className="form-group mb-3">
                    <label><strong>Investment Amount </strong></label>
                    <input
                      type="number"
                      className="form-control"
                      onWheel={(e) => e.target.blur()}
                      value={editInvestmentData.amount || ''}
                      onChange={(e) =>
                        setEditInvestmentData({ ...editInvestmentData, amount: e.target.value })
                      }
                    />
                  </div>
                  <div className="form-group mb-3">
                    <label><strong>Investment Date</strong></label>
                    <input
                      type="date"
                      className="form-control"
                      value={editInvestmentData.investmentDate || ''}
                      onChange={(e) =>
                        setEditInvestmentData({ ...editInvestmentData, investmentDate: e.target.value })
                      }
                    />
                  </div>
                  <div className="form-group mb-3">
                    <label><strong>Self ROI %</strong></label>
                    <input
                      type="number"
                      onWheel={(e) => e.target.blur()}
                      className="form-control"
                      value={editInvestmentData.self_roi_percent || ''}
                      onChange={(e) =>
                        setEditInvestmentData({ ...editInvestmentData, self_roi_percent: e.target.value })
                      }
                    />
                  </div>

                  {editInvestmentData.roi_distribution?.map((roi, idx) => (
                    <div className="form-group mb-2" key={idx}>
                      <label>
                        <strong>Level {roi.level}</strong> — {roi.user.firstName} {roi.user.lastName} (
                        {roi.user.emailId})
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        onWheel={(e) => e.target.blur()}
                        placeholder="ROI %"
                        value={roi.roi_percent}
                        onChange={(e) => {
                          const updatedROI = [...editInvestmentData.roi_distribution];
                          updatedROI[idx].roi_percent = e.target.value;
                          setEditInvestmentData({ ...editInvestmentData, roi_distribution: updatedROI });
                        }}
                      />
                    </div>
                  ))}
                  <div className="form-group mb-3">
                    <label><strong>Remarks</strong></label>
                    <textarea
                      className="form-control"
                      value={editInvestmentData.remarks || ''}
                      onChange={(e) =>
                        setEditInvestmentData({ ...editInvestmentData, remarks: e.target.value })
                      }
                    />
                  </div>
                </div>
                <div className="modal-footer">
                  <button className="btn btn-secondary" onClick={() => setEditModalVisible(false)}>
                    Cancel
                  </button>
                  <button className="btn btn-primary" onClick={handleEditInvestmentSubmit}>
                    Save Changes
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {showDetailsModal && detailsModalData && (
          <div className="modal fade show d-block" style={{ background: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog modal-xl">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title px-3 py-2">
                    Affiliate Detailed View - {detailsModalData.user.firstName} {detailsModalData.user.lastName}
                  </h5>
                  <button className="btn-close" onClick={() => setShowDetailsModal(false)}></button>
                </div>
                <div className="modal-body p-0">
                  <div className="row g-0" style={{  }}>
                    {/* Sidebar - Affiliated Data */}
                    <div className="col-md-4 border-end">
                      <div className="px-3">
                        <div className="mb-0">
                          <strong>Investments:</strong>
                          <div className="mt-2 pe-3 custom-scrollbar" style={{ maxHeight: '500px', overflowY: 'auto' }}>
                            {detailsModalData.investments && detailsModalData.investments.length > 0 ? (
                              [...detailsModalData.investments]
                                .sort((a, b) => {
                                  if (a.status === 'ACTIVE' && b.status !== 'ACTIVE') return -1;
                                  if (a.status !== 'ACTIVE' && b.status === 'ACTIVE') return 1;
                                  return 0;
                                })
                                .map((inv, idx) => {
                                const amount = typeof inv.amount === 'object'
                                  ? parseFloat(inv.amount?.$numberDecimal || 0)
                                  : parseFloat(inv.amount || 0);

                                const investmentDate = inv.investmentDate ?
                                  new Date(inv.investmentDate).toLocaleDateString() :
                                  'N/A';

                                const planName = inv.planDetails?.type || 'Select Plan';

                                return (
                                  <div key={idx} className={`card p-0 mb-3 shadow-sm ${inv?._id == selectedInvestmentForEdit?.id ? "border-warning border-2" : ""}`}>
                                    <div className="card-body p-3">
                                      <div className="d-flex justify-content-between align-items-center mb-2">
                                        <span className={`badge ${inv?.status === "ACTIVE" ? "bg-success" : inv?.status === "COMPLETED" ? "bg-warning" : "bg-danger"}`}>
                                          {planName}
                                        </span>
                                        <h6 className="card-title pb-0 mb-0">Investment ID : #{inv?.investmentId}</h6>
                                      </div>
                                      <div className="d-flex justify-content-between align-items-center mb-2">
                                        <div >
                                          <small className="text-muted">Date:</small>
                                          <div>{investmentDate}</div>
                                        </div>
                                        <div>
                                          <small className="text-muted">Amount:</small>
                                          <div className="fw-bold">{amount.toFixed(2)} {inv.currency}</div>
                                        </div>
                                      </div>

                                      <div className="d-grid">
                                        <button
                                          className="btn py-2 btn-outline-primary btn-sm"
                                          onClick={() => {
                                            console.log('Editing investment:', inv);
                                            setSelectedInvestmentForEdit({
                                              id: inv._id,
                                              plan_id: inv.plan_id,
                                              self_roi_percent: inv.self_roi_percent,
                                              currency: inv.currency,
                                              amount: inv.amount,
                                              investmentDate: inv.investmentDate,
                                              uplines: inv.roi_distribution,
                                              remarks: inv.remarks,
                                              investment_id: inv.investmentId,
                                              investor: inv.investor,
                                              status: inv.status
                                            });
                                          }}
                                        >
                                          Edit Investment
                                        </button>
                                      </div>
                                    </div>
                                  </div>
                                );
                              })
                            ) : (
                              <div className="text-center text-muted">
                                <p>No investments found.</p>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Content - Edit Investment Form */}
                    <div className="col-md-8">
                      <div className="pb-4 px-4">
                        {detailsModalData.investments && detailsModalData.investments.length > 0 ? (
                          <div className="editInvestmentForm">
                            {selectedInvestmentForEdit ? (
                              <div>
                                <div className="d-flex justify-content-between align-items-center mb-4">
                                  <h6 className="mb-0">Edit Investment #{selectedInvestmentForEdit.investment_id}</h6>
                                  <div className="d-flex align-items-center gap-3">
                                    <span className={`badge ${selectedInvestmentForEdit.status === 'ACTIVE' ? 'bg-success' : 'bg-secondary'}`}>
                                      {selectedInvestmentForEdit.status || 'INACTIVE'}
                                    </span>
                                    <div className="form-check form-switch">
                                      <input
                                        className="form-check-input"
                                        type="checkbox"
                                        role="switch"
                                        id="statusToggle"
                                        checked={selectedInvestmentForEdit.status === 'ACTIVE'}
                                        onChange={async (e) => {
                                          const newStatus = e.target.checked ? 'ACTIVE' : 'INACTIVE';
                                          if (newStatus === 'ACTIVE') {
                                            const balance = await getWalletBalance(selectedInvestmentForEdit.investor);
                                            setFundWalletBalance(balance);
                                          }
                                          setPendingStatusChange({ newStatus });
                                          setShowStatusConfirmModal(true);
                                        }}
                                      />
                                      <label className="form-check-label" htmlFor="statusToggle">
                                        {selectedInvestmentForEdit.status === 'ACTIVE' ? 'Active' : 'Inactive'}
                                      </label>
                                    </div>
                                  </div>
                                </div>

                                <div className="d-flex gap-4">
                                  <div style={{ width: '100%' }}>
                                    <div className="d-flex gap-2 mt-2">
                                      <div className="form-group mb-2 w-25">

                                        <label>Currency</label>
                                        <select className="form-select form-control shadow-soft-inner" onChange={(e) => {
                                          setPlanId("");
                                          setCurrency(e.target.value)
                                        }} value={selectedInvestmentForEdit.currency}>
                                          <option style={{ color: 'gray' }} value="INR">INR </option>
                                          <option style={{ color: 'gray' }} value="USD">USD </option>
                                          <option style={{ color: 'gray' }} value="AED">AED </option>
                                          <option style={{ color: 'gray' }} value="EUR">EUR </option>
                                        </select>
                                      </div>
                                      <div className="form-group mb-2 w-75">
                                        <label>Package</label>
                                        <select disabled={packageList?.length === 0 || selectedInvestmentForEdit.currency != "INR"} className="form-select form-control shadow-soft-inner" onChange={(e) => {
                                          const selectedPackage = packageList.find(item => item._id === e.target.value);
                                          if (selectedPackage.type === "SIP") {
                                              setSelectedInvestmentForEdit({
                                                  ...selectedInvestmentForEdit,
                                                  planDetails: selectedPackage,
                                                  plan_id: selectedPackage._id,
                                                  self_roi_percent: (selectedPackage?.return_percentage_monthly / 12)
                                              });
                                          } else {
                                            setSelectedInvestmentForEdit({
                                              ...selectedInvestmentForEdit,
                                              planDetails: selectedPackage,
                                              plan_id: selectedPackage._id,
                                              self_roi_percent: (selectedPackage?.return_percentage_monthly)
                                            });
                                          }

                                        }} value={selectedInvestmentForEdit?.plan_id || ''}>
                                          <option value="" style={{ color: 'gray' }}>Select Package </option>
                                          {packageList?.map((item) => {
                                            let duration = item?.type == "FIP" ? (item?.duration_days / 365) : item?.duration_days;
                                            return (
                                              <option key={item.plan_id} style={{ color: 'gray' }} value={item._id}>{`${item?.type} ${item?.type == "SIP" ? "" : `|| Amount: ${item?.min_amount} - ${item?.max_amount} ${item?.currency}`} || Duration : ${duration} ${duration > 1 ? "Years" : "Year"}`}</option>
                                            )
                                          })}
                                        </select>
                                      </div>
                                    </div>
                                    <div className="d-flex gap-2 my-2">
                                      <div className="form-group w-50">
                                        <label>Investment Amount ({selectedInvestmentForEdit.currency})</label>
                                        <input
                                          type="number"
                                          className="form-control"
                                          value={typeof selectedInvestmentForEdit.amount === 'object' ? selectedInvestmentForEdit.amount?.$numberDecimal : selectedInvestmentForEdit.amount}
                                          onWheel={(e) => e.target.blur()}
                                          onChange={e => {
                                            setSelectedInvestmentForEdit({
                                              ...selectedInvestmentForEdit,
                                              amount: e.target.value
                                            });
                                          }}
                                        />
                                        <button
                                          className="btn btn-sm btn-success mt-2"
                                          onClick={async () => {
                                            setFundAmount('');
                                            setRemoveAmount('');
                                            setUpdateLockedBalance(true);
                                            setFundSource('wallet');
                                            setOperationType('add');
                                            const balance = await getWalletBalance(selectedInvestmentForEdit.investor);
                                            setFundWalletBalance(balance);
                                            setShowFundModal(true);
                                          }}
                                        >
                                          Update Fund
                                        </button>
                                      </div>
                                      <div className="form-group w-50">
                                        <label>Investment Date</label>
                                        <input
                                          type="date"
                                          className="form-control"
                                          value={selectedInvestmentForEdit.investmentDate ? new Date(selectedInvestmentForEdit.investmentDate).toISOString().split('T')[0] : ''}
                                          onChange={e => {
                                            setSelectedInvestmentForEdit({
                                              ...selectedInvestmentForEdit,
                                              investmentDate: e.target.value
                                            });
                                          }}
                                        />
                                      </div>
                                    </div>
                                    <div className="form-group mb-2">
                                      <label>Self ROI %</label>
                                      <input
                                        type="number"
                                        className="form-control"
                                        value={selectedInvestmentForEdit.self_roi_percent}
                                        onWheel={(e) => e.target.blur()}
                                        onChange={e => {
                                          setSelectedInvestmentForEdit({
                                            ...selectedInvestmentForEdit,
                                            self_roi_percent: e.target.value
                                          });
                                        }}
                                      />
                                    </div>
                                    {(selectedInvestmentForEdit.uplines || []).map((up, i) => (
                                      <div className="form-group mb-2" key={i}>
                                        <label>Level {up.level} - {up.userDetails?.firstName} {up.userDetails?.lastName} ({up.userDetails?.emailId})</label>
                                        <input
                                          type="number"
                                          className="form-control"
                                          onWheel={(e) => e.target.blur()}
                                          value={up.roi_percent}
                                          onChange={e => {
                                            const updated = [...selectedInvestmentForEdit.uplines];
                                            updated[i].roi_percent = e.target.value;
                                            setSelectedInvestmentForEdit({
                                              ...selectedInvestmentForEdit,
                                              uplines: updated
                                            });
                                          }}
                                        />
                                      </div>
                                    ))}
                                    <div className="form-group mb-2">
                                      <label>Remarks</label>
                                      <input
                                        type="text"
                                        className="form-control"
                                        value={selectedInvestmentForEdit.remarks || ''}
                                        onChange={e => {
                                          setSelectedInvestmentForEdit({
                                            ...selectedInvestmentForEdit,
                                            remarks: e.target.value
                                          });
                                        }}
                                      />
                                    </div>
                                  </div>

                                </div>
                                <div className="d-flex justify-content-end gap-2 mt-4">
                                  <button
                                    className="btn btn-secondary"
                                    onClick={() => setSelectedInvestmentForEdit(null)}
                                  >
                                    Cancel
                                  </button>
                                  <button
                                    className="btn btn-primary"
                                    onClick={async () => {
                                      try {
                                        LoaderHelper.loaderStatus(true);
                                        // console.log("Editing investment:", selectedInvestmentForEdit);
                                        const payload = {
                                          investmentId: selectedInvestmentForEdit.id,
                                          amount: parseFloat(selectedInvestmentForEdit.amount),
                                          self_roi_percent: parseFloat(selectedInvestmentForEdit.self_roi_percent),
                                          roi_distribution: (selectedInvestmentForEdit.uplines || []).map(up => ({
                                            level: up.level,
                                            userId: up.user,
                                            percent: parseFloat(up.roi_percent) || 0
                                          })),
                                          plan_id: selectedInvestmentForEdit.plan_id,
                                          remarks: selectedInvestmentForEdit.remarks || '',
                                          investmentDate: selectedInvestmentForEdit.investmentDate || ""
                                        };
                                        // console.log(payload);
                                        const response = await AuthService.updateAffiliateInvestment(payload);
                                        if (response.success) {
                                          alertSuccessMessage("Investment updated successfully");
                                          loadInvestments(detailsModalData.user._id);
                                          setSelectedInvestmentForEdit(null);
                                        } else {
                                          alertErrorMessage(response.message);
                                        }
                                      } catch {
                                        alertErrorMessage("Failed to update investment");
                                      } finally {
                                        LoaderHelper.loaderStatus(false);
                                      }
                                    }}
                                  >
                                    Save Changes
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <div className="text-center text-muted">
                                <h6>Select an investment from the sidebar to edit</h6>
                                <p>Click on any "Edit Investment" button in the sidebar to start editing.</p>
                              </div>
                            )}
                          </div>
                        ) : (
                          <div className="text-center">
                            <p className="mb-3">No investments found for this affiliate.</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {showFundModal && (
          <div className="modal fade show d-block" style={{ background: 'rgba(0,0,0,0.7)', zIndex: 1060 }}>
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header px-3 py-2">
                  <h5 className="modal-title">Add Fund to Investment</h5>
                  <button className="btn-close" onClick={() => setShowFundModal(false)}></button>
                </div>
                <div className="modal-body p-3">
                  <div className="mb-3">
                    <label className="form-label">Wallet Balance:</label>
                    <div className="alert alert-info">
                      {fundWalletBalance || walletBalance} {selectedInvestmentForEdit.currency}
                    </div>
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Current Investment Amount:</label>
                    <div className="alert alert-secondary">
                      {typeof selectedInvestmentForEdit.amount === 'object' 
                        ? parseFloat(selectedInvestmentForEdit.amount?.$numberDecimal || 0).toFixed(2)
                        : parseFloat(selectedInvestmentForEdit.amount || 0).toFixed(2)
                      } {selectedInvestmentForEdit.currency}
                    </div>
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Operation Type:</label>
                    <div>
                      <div className="form-check form-check-inline">
                        <input
                          className="form-check-input"
                          type="radio"
                          id="operationTypeAdd"
                          value="add"
                          checked={operationType === 'add'}
                          onChange={(e) => {
                            setOperationType(e.target.value);
                            setFundSource('wallet');
                          }}
                        />
                        <label className="form-check-label" htmlFor="operationTypeAdd">
                          Add
                        </label>
                      </div>
                      <div className="form-check form-check-inline">
                        <input
                          className="form-check-input"
                          type="radio"
                          id="operationTypeRemove"
                          value="remove"
                          checked={operationType === 'remove'}
                          onChange={(e) => {
                            setOperationType(e.target.value);
                            setFundSource('manual');
                          }}
                        />
                        <label className="form-check-label" htmlFor="operationTypeRemove">
                          Remove
                        </label>
                      </div>
                    </div>
                  </div>
                  {operationType === 'add' && (
                    <div className="mb-3">
                      <label className="form-label">Fund Source:</label>
                      <div>
                        <div className="form-check form-check-inline">
                          <input
                            className="form-check-input"
                            type="radio"
                            id="fundSourceWallet"
                            value="wallet"
                            checked={fundSource === 'wallet'}
                            onChange={(e) => setFundSource(e.target.value)}
                          />
                          <label className="form-check-label" htmlFor="fundSourceWallet">
                            Wallet Balance
                          </label>
                        </div>
                        <div className="form-check form-check-inline">
                          <input
                            className="form-check-input"
                            type="radio"
                            id="fundSourceManual"
                            value="manual"
                            checked={fundSource === 'manual'}
                            onChange={(e) => setFundSource(e.target.value)}
                          />
                          <label className="form-check-label" htmlFor="fundSourceManual">
                            Manual
                          </label>
                        </div>
                      </div>
                    </div>
                  )}
                  {operationType === 'add' ? (
                    <div className="mb-3">
                      <label className="form-label">Amount to Add:</label>
                      <input
                        type="number"
                        className="form-control"
                        placeholder="Enter amount to add"
                        value={fundAmount}
                        onWheel={(e) => e.target.blur()}
                        onChange={(e) => setFundAmount(e.target.value)}
                      />
                    </div>
                  ) : (
                    <div className="mb-3">
                      <label className="form-label">Amount to Remove:</label>
                      <input
                        type="number"
                        className="form-control"
                        placeholder="Enter amount to remove"
                        value={removeAmount}
                        onWheel={(e) => e.target.blur()}
                        onChange={(e) => setRemoveAmount(e.target.value)}
                      />
                    </div>
                  )}
                  <div className="mb-3">
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="updateLockedBalance"
                        checked={updateLockedBalance}
                        onChange={(e) => setUpdateLockedBalance(e.target.checked)}
                      />
                      <label className="form-check-label" htmlFor="updateLockedBalance">
                        Update Locked Balance
                      </label>
                    </div>
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Total Investment Amount:</label>
                    <div className="alert alert-success">
                      {(() => {
                        const current = parseFloat(typeof selectedInvestmentForEdit.amount === 'object' 
                          ? selectedInvestmentForEdit.amount?.$numberDecimal || 0
                          : selectedInvestmentForEdit.amount || 0
                        );
                        const add = parseFloat(fundAmount || 0);
                        const remove = parseFloat(removeAmount || 0);
                        const total = operationType === 'add' ? current + add : current - remove;
                        return `${(total < 0 ? 0 : total).toFixed(2)} ${selectedInvestmentForEdit.currency}`;
                      })()}
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button 
                    className="btn btn-secondary" 
                    onClick={() => {
                      setShowFundModal(false);
                      setFundAmount('');
                      setRemoveAmount('');
                      setUpdateLockedBalance(true);
                      setFundSource('wallet');
                      setOperationType('add');
                    }}
                  >
                    Cancel
                  </button>
                  <button 
                    className="btn btn-primary"
                    onClick={handleFundUpdate}
                  >
                    Update
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {showStatusConfirmModal && pendingStatusChange && (
          <div className="modal fade show d-block" style={{ background: 'rgba(0,0,0,0.7)', zIndex: 1070 }}>
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">
                    {pendingStatusChange.newStatus === 'ACTIVE' ? 'Activate' : 'Deactivate'} Investment
                  </h5>
                  <button className="btn-close" onClick={() => {
                    setShowStatusConfirmModal(false);
                    setPendingStatusChange(null);
                  }}></button>
                </div>
                <div className="modal-body">
                  <p className="mb-3">
                    Are you sure you want to {pendingStatusChange.newStatus === 'ACTIVE' ? 'activate' : 'deactivate'} this investment?
                  </p>
                  
                  {pendingStatusChange.newStatus === 'INACTIVE' ? (
                    <div className="mb-3">
                      <div className="alert alert-info">
                        <strong>Investment Amount:</strong> {typeof selectedInvestmentForEdit.amount === 'object' 
                          ? parseFloat(selectedInvestmentForEdit.amount?.$numberDecimal || 0).toFixed(2)
                          : parseFloat(selectedInvestmentForEdit.amount || 0).toFixed(2)
                        } {selectedInvestmentForEdit.currency}
                      </div>
                      <div className="form-check">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id="releaseToWallet"
                          checked={releaseToWallet}
                          onChange={(e) => setReleaseToWallet(e.target.checked)}
                        />
                        <label className="form-check-label" htmlFor="releaseToWallet">
                          Release amount to wallet balance
                        </label>
                      </div>
                    </div>
                  ) : (
                    <div className="mb-3">
                      <div className="alert alert-info">
                        <strong>Wallet Balance:</strong> {fundWalletBalance || walletBalance} {selectedInvestmentForEdit.currency}
                      </div>
                      <div className="alert alert-warning">
                        <strong>Investment Amount:</strong> {typeof selectedInvestmentForEdit.amount === 'object' 
                          ? parseFloat(selectedInvestmentForEdit.amount?.$numberDecimal || 0).toFixed(2)
                          : parseFloat(selectedInvestmentForEdit.amount || 0).toFixed(2)
                        } {selectedInvestmentForEdit.currency}
                      </div>
                      <div className="form-check">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id="deductFromWallet"
                          checked={deductFromWallet}
                          onChange={(e) => setDeductFromWallet(e.target.checked)}
                        />
                        <label className="form-check-label" htmlFor="deductFromWallet">
                          Deduct amount from wallet balance
                        </label>
                      </div>
                    </div>
                  )}
                </div>
                <div className="modal-footer">
                  <button 
                    className="btn btn-secondary" 
                    onClick={() => {
                      setShowStatusConfirmModal(false);
                      setPendingStatusChange(null);
                      setReleaseToWallet(true);
                      setDeductFromWallet(true);
                    }}
                  >
                    No
                  </button>
                  <button 
                    className="btn btn-primary"
                    onClick={handleStatusToggle}
                  >
                    Yes
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {showStatusConfirmModal && pendingStatusChange && (
          <div className="modal fade show d-block" style={{ background: 'rgba(0,0,0,0.7)', zIndex: 1070 }}>
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content p-3">
                <div className="modal-header">
                  <h5 className="modal-title">
                    {pendingStatusChange.newStatus === 'ACTIVE' ? 'Activate' : 'Deactivate'} Investment
                  </h5>
                  <button className="btn-close" onClick={() => {
                    setShowStatusConfirmModal(false);
                    setPendingStatusChange(null);
                  }}></button>
                </div>
                <div className="modal-body">
                  <p className="mb-3">
                    Are you sure you want to {pendingStatusChange.newStatus === 'ACTIVE' ? 'activate' : 'deactivate'} this investment?
                  </p>
                  
                  {pendingStatusChange.newStatus === 'INACTIVE' ? (
                    <div className="mb-3">
                      <div className="alert alert-info">
                        <strong>Investment Amount:</strong> {typeof selectedInvestmentForEdit.amount === 'object' 
                          ? parseFloat(selectedInvestmentForEdit.amount?.$numberDecimal || 0).toFixed(2)
                          : parseFloat(selectedInvestmentForEdit.amount || 0).toFixed(2)
                        } {selectedInvestmentForEdit.currency}
                      </div>
                      <div className="form-check">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id="releaseToWallet"
                          checked={releaseToWallet}
                          onChange={(e) => setReleaseToWallet(e.target.checked)}
                        />
                        <label className="form-check-label" htmlFor="releaseToWallet">
                          Release amount to wallet balance
                        </label>
                      </div>
                    </div>
                  ) : (
                    <div className="mb-3">
                      <div className="alert alert-info">
                        <strong>Wallet Balance:</strong> {fundWalletBalance || walletBalance} {selectedInvestmentForEdit.currency}
                      </div>
                      <div className="alert alert-warning">
                        <strong>Investment Amount:</strong> {typeof selectedInvestmentForEdit.amount === 'object' 
                          ? parseFloat(selectedInvestmentForEdit.amount?.$numberDecimal || 0).toFixed(2)
                          : parseFloat(selectedInvestmentForEdit.amount || 0).toFixed(2)
                        } {selectedInvestmentForEdit.currency}
                      </div>
                      <div className="form-check">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id="deductFromWallet"
                          checked={deductFromWallet}
                          onChange={(e) => setDeductFromWallet(e.target.checked)}
                        />
                        <label className="form-check-label" htmlFor="deductFromWallet">
                          Deduct amount from wallet balance
                        </label>
                      </div>
                    </div>
                  )}
                </div>
                <div className="modal-footer">
                  <button 
                    className="btn btn-secondary" 
                    onClick={() => {
                      setShowStatusConfirmModal(false);
                      setPendingStatusChange(null);
                      setReleaseToWallet(true);
                      setDeductFromWallet(true);
                    }}
                  >
                    No
                  </button>
                  <button 
                    className="btn btn-primary"
                    onClick={handleStatusToggle}
                  >
                    Yes
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {showPayoutModal && payoutModalUser && (
          <div className="modal fade show d-block" style={{ background: 'rgba(0,0,0,0.7)', zIndex: 1070 }}>
            <div className="modal-dialog modal-xl modal-dialog-centered" style={{ maxWidth: '90%' }}>
              <div className="modal-content">
                <div className="modal-header px-5 py-3">
                  <h5 className="modal-title my-0">
                    Payout Details - {payoutModalUser.firstName} {payoutModalUser.lastName}
                  </h5>
                  <button 
                    className="btn-close" 
                    onClick={() => {
                      setShowPayoutModal(false);
                      setPayoutModalUser(null);
                    }}
                  ></button>
                </div>
                <div className="modal-body p-0" style={{ height: 'calc(90vh - 60px)', overflow: 'auto' }}>
                  <ReferralTree userId={payoutModalUser._id} />
                </div>
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default PayoutList;